import React, { useState, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import { FaCheck } from "react-icons/fa";
import { FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import U2000ComboBoxBase from "../../../formControls/combobox/U2000ComboBoxBase";
import U2000SearchCriteriaDateField from "./U2000SearchCriteriaDateField";
import type { U2000TableColumn } from "../../basic/U2000BasicTableList";
import U2000InputTextBase from "../../../formControls/input/U2000InputTextBase";
import type { U2000Condition, U2000ConditionType } from "../../basic/U2000BasicSearch";
import U2000SearchBooleanValueCombo, { normalizeBooleanCriteriaValue } from "./U2000SearchBooleanValueCombo";
import U2000CriteriaStaticOptionsCombo from "./U2000CriteriaStaticOptionsCombo";
import U2000CriteriaInitOptionsCombo from "./U2000CriteriaInitOptionsCombo";
import U2000CriteriaOptionsComboIn from "./U2000CriteriaOptionsComboIn";
import { resolveCriteriaInitOptions } from "./U2000CriteriaInitOptionsRegistry";
import type { U2000CriteriaStaticOption } from "../../basic/U2000BasicTableList";
import U2000CriteriaAutoComplete from "./U2000CriteriaAutoComplete";
import U2000CriteriaFreeTextTagsInput from "./U2000CriteriaFreeTextTagsInput";
import U2000TagAutocomplete from "./U2000TagAutocomplete";
import {
  isCriteriaLookupAutocompleteOperator,
  resolveCriteriaLookup,
  resolveItemToSelectedDisplay,
} from "./U2000CriteriaLookupRegistry";
import {
  parseCsvToTags,
  type U2000CriteriaTag,
} from "./u2000CriteriaTags";
import {
  type DateCriteriaRange,
  isCriteriaDateColumn,
  isDateSingleValueOperator,
  isIntervalDateOperator,
  isIntervalRangeComplete,
  parseIntervalValue,
} from "./u2000DateCriteriaUtils";
import U2000SearchCriteriaIntervalField from "./U2000SearchCriteriaIntervalField";
import {
  isFreeTextStringColumn,
  resolveStringCriteriaEditorMode,
} from "./u2000StringCriteriaEditorUtils";
import { resolveU2000InputTagFilter } from "../../../../tools/u2000InputTagFilters";
import {
  resolveFieldConditionsForColumn,
  u2000CoerceLookupCriteriaCondition,
  u2000SortCriteriaColumns,
} from "../../../../tools/u2000SearchCriteriaFieldConditions";
import { useToast } from "../../../../context/toast/U2000ToastContext";
import {
  hasDraftCriteriaContent,
  isDraftCriteriaComplete,
  serializeDraftCriteria,
  type U2000DraftCriteriaState,
} from "./u2000DraftCriteriaUtils";

export type U2000DraftCriteriaFlushResult = "committed" | "reset" | "noop";

export interface U2000BasicSearchFieldCriteriaLineHandle {
  flushDraftCriteriaOnSearch(): U2000DraftCriteriaFlushResult;
}

interface U2000BasicSearchFieldCriteriaLineProps {
  columns: U2000TableColumn[];
  conditionTypes: U2000ConditionType[];
  onAddCondition: (field: U2000TableColumn, condition: U2000Condition, value: string | null, displayValue?: string) => void;
  selectedItem?: {
    field: U2000TableColumn;
    condition: U2000Condition;
    value: string;
    displayValue?: string;
  } | null;
  onCancelEdit?: () => void;
}

interface CalendarPickerValue {
  value: string;
  timestamp: number;
}

function hasCriteriaLookup(field: U2000TableColumn | null): boolean {
  return field?.criteriaLookup != null && resolveCriteriaLookup(field.criteriaLookup.findMethod) != null;
}

function isInOperator(condition: U2000Condition | null): boolean {
  return condition?.name === "in";
}

function isLookupSingleOperator(condition: U2000Condition | null): boolean {
  return condition?.name === "equals" || condition?.name === "notequals";
}

function useLookupAutocomplete(field: U2000TableColumn | null, condition: U2000Condition | null): boolean {
  return hasCriteriaLookup(field) && isCriteriaLookupAutocompleteOperator(condition?.name);
}

function hasCriteriaInitOptions(field: U2000TableColumn | null): boolean {
  return field?.criteriaInitOptions != null;
}

function hasCriteriaStaticOptions(field: U2000TableColumn | null): boolean {
  return (field?.criteriaStaticOptions?.length ?? 0) > 0;
}

function hasCriteriaOptionsCombo(field: U2000TableColumn | null): boolean {
  return hasCriteriaInitOptions(field) || hasCriteriaStaticOptions(field);
}

function getCriteriaOptionsForField(field: U2000TableColumn): U2000CriteriaStaticOption[] {
  if (field.criteriaStaticOptions?.length) {
    return field.criteriaStaticOptions;
  }
  if (field.criteriaInitOptions) {
    return resolveCriteriaInitOptions(field.criteriaInitOptions.optionsKey);
  }
  return [];
}

const U2000BasicSearchFieldCriteriaLine = forwardRef<U2000BasicSearchFieldCriteriaLineHandle, U2000BasicSearchFieldCriteriaLineProps>(
  ({ columns, conditionTypes, onAddCondition, selectedItem, onCancelEdit }, ref) => {
  const { t, i18n } = useTranslation("common");
  const toast = useToast();
  const locale = i18n.language;

  const sortedCriteriaColumns = useMemo(
    () => u2000SortCriteriaColumns(columns, locale),
    [columns, locale],
  );

  React.useEffect(() => {
    if (!selectedItem) {
      setField(null);
      setCondition(null);
      setValue(null);
      setDisplayValue(undefined);
      setLookupTags([]);
      setCalendarPickerValue({ value: "", timestamp: Date.now() });
      setIntervalRange({});
      setFieldConditions(null);
    }
  }, [selectedItem]);

  const [field, setField] = useState<U2000TableColumn | null>(null);
  const [condition, setCondition] = useState<U2000Condition | null>(null);
  const [value, setValue] = useState<string | null>(null);
  const [displayValue, setDisplayValue] = useState<string | undefined>(undefined);
  const [lookupTags, setLookupTags] = useState<U2000CriteriaTag[]>([]);
  const [intervalRange, setIntervalRange] = useState<DateCriteriaRange>({});
  const [calendarPickerValue, setCalendarPickerValue] = useState<CalendarPickerValue>({
    value: "",
    timestamp: Date.now(),
  });

  const lookupActive = useLookupAutocomplete(field, condition);
  const lookupInMode = lookupActive && isInOperator(condition);
  const lookupSingleMode = lookupActive && isLookupSingleOperator(condition);
  const comboInMode = hasCriteriaOptionsCombo(field) && isInOperator(condition);
  const comboSingleMode = hasCriteriaOptionsCombo(field) && isLookupSingleOperator(condition);
  const stringEditorMode = resolveStringCriteriaEditorMode(field, condition?.name);
  const stringInMode = stringEditorMode === "in";
  const stringTagFilter = resolveU2000InputTagFilter(field?.criteriaInputTagType);
  const showDateInterval = isIntervalDateOperator(condition?.name);
  const showDateSingle = isCriteriaDateColumn(field) && isDateSingleValueOperator(condition?.name);

  const draftState = useMemo<U2000DraftCriteriaState>(
    () => ({
      field,
      condition,
      value,
      displayValue,
      lookupTags,
      intervalRange,
      calendarPickerValue,
    }),
    [field, condition, value, displayValue, lookupTags, intervalRange, calendarPickerValue],
  );

  const isActionEnabled = isDraftCriteriaComplete(draftState);

  function resetDraftCriteriaLocal(): void {
    setField(null);
    setCondition(null);
    setValue(null);
    setDisplayValue(undefined);
    setLookupTags([]);
    setCalendarPickerValue({ value: "", timestamp: Date.now() });
    setIntervalRange({});
    setFieldConditions(null);
  }

  function resetCriteriaHandler() {
    resetDraftCriteriaLocal();
    if (onCancelEdit) onCancelEdit();
  }

  function flushDraftCriteriaOnSearch(): U2000DraftCriteriaFlushResult {
    if (!hasDraftCriteriaContent(draftState)) {
      return "noop";
    }
    const serialized = serializeDraftCriteria(draftState);
    if (serialized) {
      onAddCondition(serialized.field, serialized.condition, serialized.value, serialized.displayValue);
      resetDraftCriteriaLocal();
      return "committed";
    }
    resetCriteriaHandler();
    return "reset";
  }

  useImperativeHandle(ref, () => ({ flushDraftCriteriaOnSearch }), [draftState, onAddCondition, onCancelEdit]);

  React.useEffect(() => {
    if (selectedItem) {
      const coercedCondition = u2000CoerceLookupCriteriaCondition(
        selectedItem.field,
        selectedItem.condition,
        conditionTypes,
        locale,
      );
      setField(selectedItem.field);
      setCondition(coercedCondition);
      if (isIntervalDateOperator(coercedCondition.name)) {
        setIntervalRange(parseIntervalValue(selectedItem.value));
        setValue(null);
        setCalendarPickerValue({ value: "", timestamp: Date.now() });
      } else if (isCriteriaDateColumn(selectedItem.field) && isDateSingleValueOperator(coercedCondition.name)) {
        const parsedCalendar =
          typeof selectedItem.value === "object" && selectedItem.value !== null && "value" in selectedItem.value && "timestamp" in selectedItem.value
            ? selectedItem.value
            : { value: selectedItem.value as string, timestamp: Date.now() };
        setCalendarPickerValue(parsedCalendar);
        setValue(parsedCalendar.value);
        setIntervalRange({});
      } else if (isCriteriaDateColumn(selectedItem.field)) {
        setCalendarPickerValue({ value: "", timestamp: Date.now() });
        setIntervalRange({});
      } else if (selectedItem.field.type === "boolean") {
        setValue(normalizeBooleanCriteriaValue(selectedItem.value) ?? "");
        setDisplayValue(undefined);
        setLookupTags([]);
      } else if (
        (hasCriteriaLookup(selectedItem.field) || hasCriteriaOptionsCombo(selectedItem.field)) &&
        isInOperator(coercedCondition)
      ) {
        setLookupTags(parseCsvToTags(selectedItem.value, selectedItem.displayValue));
        setValue(null);
        setDisplayValue(undefined);
      } else if (isFreeTextStringColumn(selectedItem.field) && isInOperator(coercedCondition)) {
        setLookupTags(parseCsvToTags(selectedItem.value, selectedItem.displayValue));
        setValue(null);
        setDisplayValue(undefined);
      } else if (hasCriteriaOptionsCombo(selectedItem.field) && isLookupSingleOperator(coercedCondition)) {
        setValue(selectedItem.value);
        setDisplayValue(selectedItem.displayValue);
        setLookupTags([]);
      } else if (hasCriteriaLookup(selectedItem.field) && isLookupSingleOperator(coercedCondition)) {
        setValue(selectedItem.value);
        setDisplayValue(selectedItem.displayValue);
        setLookupTags([]);
      } else {
        setValue(selectedItem.value);
        setDisplayValue(undefined);
        setLookupTags([]);
      }
      setFieldConditions(resolveFieldConditionsForColumn(selectedItem.field, conditionTypes, locale));
    }
  }, [selectedItem, conditionTypes, locale]);

  const [fieldConditions, setFieldConditions] = useState<U2000Condition[] | null>(null);

  function onFieldChangeObject(nextField: U2000TableColumn | null) {
    if (!nextField) {
      setField(null);
      setCondition(null);
      setValue(null);
      setDisplayValue(undefined);
      setLookupTags([]);
      setFieldConditions(null);
      return;
    }

    if (nextField.type === "date") {
      setCalendarPickerValue({ value: "", timestamp: Date.now() });
    }
    setIntervalRange({});
    setField(nextField);
    setCondition(null);
    setValue("");
    setDisplayValue(undefined);
    setLookupTags([]);
    setFieldConditions(resolveFieldConditionsForColumn(nextField, conditionTypes, locale));
  }

  function onConditionChangeValue(nextCondition: U2000Condition | null) {
    setCondition(nextCondition);
    setValue("");
    setDisplayValue(undefined);
    setLookupTags([]);
    setIntervalRange({});
    setCalendarPickerValue({ value: "", timestamp: Date.now() });
  }

  function onValueChange(nextValue: string) {
    setValue(nextValue);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      addCriteriaHandler();
    }
  }

  function isDisableInputValue() {
    return condition == null || !condition.isRequiredValue;
  }

  const lookupHandler = field?.criteriaLookup ? resolveCriteriaLookup(field.criteriaLookup.findMethod) : undefined;
  const lookupConfig = field?.criteriaLookup;
  const addCriteriaButtonRef = useRef<HTMLButtonElement>(null);
  const focusAddCriteriaButton = useCallback(() => {
    addCriteriaButtonRef.current?.focus();
  }, []);

  function renderValueInput(): React.ReactNode {
    if (field == null || condition == null) {
      return (
        <U2000InputTextBase fieldName="value" type="string" disabled value="" setValue={() => undefined} placeholder="" />
      );
    }

    const lookupPlaceholder = lookupConfig?.placeholder ?? t("lblSearchCriteriaLookupPlaceholder");

    if (lookupInMode && lookupHandler && lookupConfig) {
      return (
        <U2000TagAutocomplete
          value={lookupTags}
          onChange={setLookupTags}
          disabled={isDisableInputValue()}
          allowFreeText={false}
          placeholder={lookupPlaceholder}
          searchingLabel={t("lblSearchCriteriaLookupSearching")}
          noResultsLabel={(query) => t("lblSearchCriteriaLookupNoResults", { query })}
          searchCriterionField={lookupConfig.searchCriterionField}
          onSearch={lookupHandler.search}
          getTagKey={(tag) => tag.value}
          getTagLabel={(tag) => tag.label ?? tag.value}
          itemToTag={(item) => {
            const v = lookupHandler.itemToValue(item, lookupConfig);
            if (!v) return null;
            return { value: v, label: resolveItemToSelectedDisplay(lookupHandler, item, lookupConfig) };
          }}
          renderSuggestion={(item) => lookupHandler.itemToDisplay(item, lookupConfig)}
          onMaxTagsError={(max) => toast.error(t("errSearchCriteriaMaxTags", { max }))}
          onDuplicateError={() => toast.error(t("errSearchCriteriaDuplicateTag"))}
          onAfterSelect={focusAddCriteriaButton}
        />
      );
    }

    if (lookupSingleMode && lookupHandler && lookupConfig) {
      return (
        <U2000CriteriaAutoComplete
          value={value ?? ""}
          displayValue={displayValue}
          disabled={isDisableInputValue()}
          placeholder={lookupPlaceholder}
          searchingLabel={t("lblSearchCriteriaLookupSearching")}
          noResultsLabel={(query) => t("lblSearchCriteriaLookupNoResults", { query })}
          searchCriterionField={lookupConfig.searchCriterionField}
          onSearch={lookupHandler.search}
          itemToValue={(item) => lookupHandler.itemToValue(item, lookupConfig)}
          itemToDisplay={(item) => lookupHandler.itemToDisplay(item, lookupConfig)}
          itemToSelectedDisplay={(item) => resolveItemToSelectedDisplay(lookupHandler, item, lookupConfig)}
          onSelect={(v, d) => {
            setValue(v);
            setDisplayValue(d || undefined);
          }}
          onAfterSelect={focusAddCriteriaButton}
        />
      );
    }

    if (isCriteriaDateColumn(field)) {
      if (showDateInterval) {
        return <U2000SearchCriteriaIntervalField range={intervalRange} onRangeChange={setIntervalRange} disabled={isDisableInputValue()} />;
      }
      if (showDateSingle) {
        return (
          <U2000SearchCriteriaDateField
            value={calendarPickerValue}
            onValueChange={(dateValue) => {
              onValueChange(dateValue);
              setCalendarPickerValue({ value: dateValue, timestamp: Date.now() });
            }}
            disabled={isDisableInputValue()}
            dateFormat="yyyy-MM-dd"
            placeHolder="yyyy-MM-dd"
          />
        );
      }
      if (!condition.isRequiredValue) {
        return <div className="min-h-[46px] rounded-lg border border-dashed border-gray-200 bg-gray-50/80" aria-hidden />;
      }
      return <div className="min-h-[46px] rounded-lg border border-dashed border-gray-200 bg-gray-50/80" aria-hidden />;
    }

    if (field.type === "boolean") {
      return <U2000SearchBooleanValueCombo value={value} onChange={onValueChange} disabled={isDisableInputValue()} />;
    }

    if (comboInMode) {
      return (
        <U2000CriteriaOptionsComboIn
          options={getCriteriaOptionsForField(field)}
          value={lookupTags}
          onChange={setLookupTags}
          disabled={isDisableInputValue()}
          placeholder={t("lblSearchCriteriaComboInPlaceholder")}
          onAfterSelect={focusAddCriteriaButton}
        />
      );
    }

    if (comboSingleMode && hasCriteriaInitOptions(field) && field.criteriaInitOptions) {
      return (
        <U2000CriteriaInitOptionsCombo
          config={field.criteriaInitOptions}
          value={value}
          onChange={(v, label) => {
            onValueChange(v);
            setDisplayValue(label);
          }}
          disabled={isDisableInputValue()}
        />
      );
    }

    if (comboSingleMode && hasCriteriaStaticOptions(field) && field.criteriaStaticOptions) {
      return (
        <U2000CriteriaStaticOptionsCombo
          options={field.criteriaStaticOptions}
          value={value}
          onChange={(v, label) => {
            onValueChange(v);
            setDisplayValue(label);
          }}
          disabled={isDisableInputValue()}
        />
      );
    }

    if (stringInMode) {
      return (
        <U2000CriteriaFreeTextTagsInput
          value={lookupTags}
          onChange={setLookupTags}
          disabled={isDisableInputValue()}
          placeholder={t("lblSearchCriteriaStringTagsPlaceholder")}
          filterFunction={stringTagFilter}
          onAfterSelect={focusAddCriteriaButton}
          onMaxTagsError={(max) => toast.error(t("errSearchCriteriaMaxTags", { max }))}
          onDuplicateError={() => toast.error(t("errSearchCriteriaDuplicateTag"))}
        />
      );
    }

    if (stringEditorMode === "none" && !condition.isRequiredValue) {
      return <div className="min-h-[46px] rounded-lg border border-dashed border-gray-200 bg-gray-50/80" aria-hidden />;
    }

    return (
      <U2000InputTextBase
        fieldName="value"
        type="string"
        disabled={isDisableInputValue()}
        value={value as string}
        setValue={setValue as (value: string | number) => void}
        onKeyDown={onKeyDown}
      />
    );
  }

  function addCriteriaHandler() {
    const serialized = serializeDraftCriteria(draftState);
    if (!serialized) {
      if (isIntervalDateOperator(condition?.name) && !isIntervalRangeComplete(intervalRange)) {
        toast.error(t("errSearchCriteriaIntervalIncomplete"));
      }
      return;
    }
    onAddCondition(serialized.field, serialized.condition, serialized.value, serialized.displayValue);
  }

  return (
    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200 shadow-xs">
      <div className="flex-1">
        <U2000ComboBoxBase<U2000TableColumn> data={sortedCriteriaColumns} keyField="name" descField="label" value={field} onChangeObject={(f) => onFieldChangeObject(f)} disabled={false} />
      </div>

      <div className="flex-1">
        <U2000ComboBoxBase<U2000Condition>
          data={fieldConditions || []}
          keyField="name"
          descField="label"
          value={condition}
          onChangeObject={(c) => onConditionChangeValue(c)}
          disabled={field === null}
        />
      </div>

      <div className="min-h-[46px] flex-1">{renderValueInput()}</div>

      <div className="flex items-center gap-2">
        <button
          ref={addCriteriaButtonRef}
          className={`flex items-center justify-center h-10 w-10 rounded-lg transition-all duration-200 ease-in-out
                    focus:outline-hidden focus:ring-2 focus:ring-green-500/20
                    ${isActionEnabled ? "text-green-600 hover:text-white hover:bg-green-600 shadow-xs hover:shadow-md active:scale-95" : "text-gray-300 cursor-not-allowed bg-gray-100"}`}
          onClick={addCriteriaHandler}
          disabled={!isActionEnabled}
          title="Ajouter le critère"
          aria-label="Ajouter le critère"
        >
          <FaCheck className="h-4 w-4" />
        </button>
        <button
          className="flex items-center justify-center h-10 w-10 text-red-600 hover:text-white hover:bg-red-600
                   rounded-lg transition-all duration-200 ease-in-out shadow-xs hover:shadow-md
                   focus:outline-hidden focus:ring-2 focus:ring-red-500/20 active:scale-95"
          onClick={resetCriteriaHandler}
          title="Réinitialiser le critère"
          aria-label="Réinitialiser le critère"
        >
          <FaTimes className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
});

U2000BasicSearchFieldCriteriaLine.displayName = "U2000BasicSearchFieldCriteriaLine";

export default U2000BasicSearchFieldCriteriaLine;
